// The shell the Unassigned faces and Unassigned voices tabs share: the heading with the count,
// the selection's actions, the list the tab hands in, the pager, and the two dialogs — a new
// identity from the selection, or an existing one picked from the case. The words that name
// what is selected come from `copy`.
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AssignFacesDialog } from '@/components/identities/AssignFacesDialog'
import {
  type CreateIdentityDraft,
  CreateIdentityFromFacesDialog,
  EMPTY_IDENTITY_DRAFT,
  identityNamesFromDraft,
} from '@/components/identities/CreateIdentityFromFacesDialog'
import { Button } from '@/components/ui/button'
import { useIdentityPicker } from './hooks/useIdentityPicker'
import type { UnassignedSelection } from './hooks/useUnassignedSelection'

/** The pager and the selection words are the faces tab's; every tab shares them. */
const SHARED = 'identity.unassigned'

export interface UnassignedSelectionShellProps {
  caseId: string
  /** The i18n base of the tab: `title`, `summary`, optional `hint`, `writeFailed`, `assign.*`, `create.*`. */
  copy: string
  selection: UnassignedSelection<unknown, unknown>
  /** The caller may decide; a viewer gets the list without the actions. */
  canDecide: boolean
  children: ReactNode
}

export function UnassignedSelectionShell({
  caseId,
  copy,
  selection,
  canDecide,
  children,
}: UnassignedSelectionShellProps) {
  const { t } = useTranslation()
  const [createOpen, setCreateOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [draft, setDraft] = useState<CreateIdentityDraft>(EMPTY_IDENTITY_DRAFT)
  const [pickedId, setPickedId] = useState<string | null>(null)
  const picker = useIdentityPicker(caseId, assignOpen)

  const selectedCount = selection.selected.length
  // An unexplained failure keeps its own sentence out of the server's hands.
  const refusal = selection.error === null ? null : selection.error || t(`${copy}.writeFailed`)
  const hint = t(`${copy}.hint`, { defaultValue: '' })

  const openCreate = () => {
    selection.clearError()
    setDraft(EMPTY_IDENTITY_DRAFT)
    setCreateOpen(true)
  }
  const openAssign = () => {
    selection.clearError()
    setPickedId(null)
    setAssignOpen(true)
  }
  const submitCreate = async () => {
    if (await selection.createIdentity(identityNamesFromDraft(draft))) setCreateOpen(false)
  }
  const submitAssign = async () => {
    if (pickedId && (await selection.assignToIdentity(pickedId))) setAssignOpen(false)
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t(`${copy}.title`)}</h2>
          <p className="text-sm text-muted-foreground">
            {t(`${copy}.summary`, { count: selection.total })}
          </p>
          {hint ? <p className="mt-1 text-sm text-muted-foreground">{hint}</p> : null}
        </div>
        {canDecide ? (
          /* On a phone the count takes its own line and the actions share the
             full width under it. */
          <div className="flex flex-wrap items-center gap-2 max-md:w-full max-md:[&>button]:flex-1 md:flex-nowrap">
            <span className="text-sm text-muted-foreground max-md:basis-full">
              {t(`${SHARED}.selected`, { count: selectedCount })}
            </span>
            <Button
              variant="outline"
              onClick={selection.clearSelection}
              disabled={selectedCount === 0}
            >
              {t(`${SHARED}.clearSelection`)}
            </Button>
            <Button variant="outline" onClick={openAssign} disabled={selectedCount === 0}>
              {t(`${copy}.assign.open`)}
            </Button>
            <Button onClick={openCreate} disabled={selectedCount === 0}>
              {t(`${copy}.create.open`)}
            </Button>
          </div>
        ) : null}
      </div>

      {refusal && !createOpen && !assignOpen ? (
        <p role="alert" className="text-sm text-destructive">
          {refusal}
        </p>
      ) : null}

      {children}

      {selection.totalPages > 1 ? (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => selection.setPage(selection.page - 1)}
            disabled={selection.page <= 1}
          >
            {t(`${SHARED}.previous`)}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t(`${SHARED}.pageOf`, { page: selection.page, total: selection.totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => selection.setPage(selection.page + 1)}
            disabled={selection.page >= selection.totalPages}
          >
            {t(`${SHARED}.next`)}
          </Button>
        </div>
      ) : null}

      <CreateIdentityFromFacesDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        faceCount={selectedCount}
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={() => void submitCreate()}
        isSubmitting={selection.isSubmitting}
        error={refusal}
        copy={`${copy}.create`}
      />
      <AssignFacesDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        faceCount={selectedCount}
        identities={picker.options}
        isLoading={picker.isLoading}
        query={picker.query}
        onQueryChange={picker.setQuery}
        selectedId={pickedId}
        onSelect={setPickedId}
        onSubmit={() => void submitAssign()}
        isSubmitting={selection.isSubmitting}
        error={refusal}
        copy={`${copy}.assign`}
      />
    </div>
  )
}
